import { BoundSelectCardGroupField, Css, useComputed } from "@homebound/beam";
import { useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { GuidedChangeEventFormState } from "src/routes/projects/change-events/guided/CreateGuidedChangeEventPage";
import {
  NewConfigurationSection_CheckoutConfigFragment,
  NewConfigurationSection_ProductOfferingConfigFragment,
  NewConfigurationSection_StructureFragment,
  useNewConfigurationApplicableConfigsQuery,
} from "src/routes/projects/change-events/guided/components/NewConfigurationSection.generated";
import { GuidedChangeEventConfigType } from "src/routes/projects/change-events/guided/utils";
import { fail, queryResult } from "src/utils";
import { ReadyPlanConfigCardDetailContent } from "./ReadyPlanConfigCard";

type NewConfigurationSectionProps = {
  formState: GuidedChangeEventFormState;
  structure: NewConfigurationSection_StructureFragment;
};
export function NewConfigurationSection(props: NewConfigurationSectionProps) {
  const { formState, structure } = props;
  const [search, setSearch] = useState("");
  return (
    <div css={Css.df.fdc.gap3.$}>
      <h3 css={Css.lg.$}>New Configuration</h3>
      <BoundSelectCardGroupField
        field={formState.selectedConfigType}
        label="Type"
        view="list"
        options={[
          {
            value: GuidedChangeEventConfigType.CheckoutConfig,
            label: "Checkout Config",
            description:
              "Use a homeowner-approved configuration created specifically for this structure. Includes project-specific pricing.",
          },
          {
            value: GuidedChangeEventConfigType.ProductOfferingConfig,
            label: "Product Offering Config",
            description:
              "Use a standard product offering from this community. Applies the latest catalog definition and pricing.",
          },
        ]}
        onChange={(value) => {
          formState.selectedConfigType.set(value);
          // Changing the type clears any previously selected config
          formState.selectedConfigId.set(undefined);
        }}
      />
      <NewConfigurationSectionView formState={formState} structure={structure} search={search} setSearch={setSearch} />
    </div>
  );
}

type NewConfigurationSectionViewProps = {
  formState: GuidedChangeEventFormState;
  structure: NewConfigurationSection_StructureFragment;
  search: string;
  setSearch: (search: string) => void;
};
function NewConfigurationSectionView(props: NewConfigurationSectionViewProps) {
  const { formState, structure, search, setSearch } = props;
  const selectedConfigType = useComputed(() => formState.selectedConfigType.value, [formState]);
  const productOfferingId = structure.readyPlanConfig?.productOffering?.id ?? fail("Product offering ID is required");
  const query = useNewConfigurationApplicableConfigsQuery({
    skip: !selectedConfigType,
    variables: {
      search,
      productOfferingId,
      structureId: structure.id,
      includeCheckoutConfigs: selectedConfigType === GuidedChangeEventConfigType.CheckoutConfig,
      includeProductOfferingConfigs: selectedConfigType === GuidedChangeEventConfigType.ProductOfferingConfig,
    },
  });
  return (
    <div css={Css.df.fdc.gap2.$}>
      <div css={Css.df.jcsb.aic.$}>
        <span css={Css.sm.fw6.$}>Configurations</span>
        <SearchBox onSearch={setSearch} />
      </div>
      {!selectedConfigType && (
        <EmptyConfigsState message="Select a configuration type above to see the configurations populated here." />
      )}
      {queryResult(query, ({ checkoutConfigs, productOfferingConfigs }) => (
        <ConfigurationSelector
          structure={structure}
          formState={formState}
          productOfferingConfigs={productOfferingConfigs?.entities ?? []}
          checkoutConfigs={checkoutConfigs?.entities ?? []}
        />
      ))}
    </div>
  );
}

type ConfigurationSelectorProps = {
  formState: GuidedChangeEventFormState;
  structure: NewConfigurationSection_StructureFragment;
  productOfferingConfigs: NewConfigurationSection_ProductOfferingConfigFragment[];
  checkoutConfigs: NewConfigurationSection_CheckoutConfigFragment[];
};
function ConfigurationSelector(props: ConfigurationSelectorProps) {
  const { formState, structure, productOfferingConfigs, checkoutConfigs } = props;
  const applicableConfigOptions = useMemo(
    () =>
      [
        // Show active product offerings only
        ...productOfferingConfigs.filter((poc) => poc.productOffering.status.code === ReadyPlanStatus.Active),
        ...checkoutConfigs,
      ].map((configLike) => ({
        value: configLike.id,
        label: configLike?.name ?? "(no name)",
        description: <ReadyPlanConfigCardDetailContent readyPlanConfig={configLike} structure={structure} />,
      })),
    [checkoutConfigs, productOfferingConfigs, structure],
  );
  if (applicableConfigOptions.isEmpty) {
    return <EmptyConfigsState message="There are no configurations available for this structure yet." />;
  } else {
    return (
      <BoundSelectCardGroupField
        view="list"
        labelStyle="hidden"
        options={applicableConfigOptions}
        field={formState.selectedConfigId}
      />
    );
  }
}

function EmptyConfigsState(props: { message: string }) {
  return <div css={Css.df.fdc.gap2.jcc.$}>{props.message}</div>;
}
